/**
 * 缩略图 URL 构建（**客户端安全**，可 import 进 "use client" 组件）。
 *
 * `uploads/xxx.png` → `/api/uploads/thumb/xxx.png?w=480`（自动带 basePath）。
 *
 * 实现上直接复用 {@link uploadUrl} 的归一化与前缀逻辑，再把路径里的
 * `/api/uploads/` 段替换为 `/api/uploads/thumb/` —— 该子串在两种形态
 * （带/不带 mount 前缀）下都唯一，单次替换即可，不会误伤。
 *
 * 非 `/api/uploads/` 的 URL（外链、data URL 等）原样返回，调用方无需判断。
 */
import { uploadUrl } from "@/lib/utils/upload-url";

export type ThumbWidth = 320 | 480 | 960;

export function thumbUrl(filePath: string, width: ThumbWidth = 480): string {
  if (!filePath) return "";
  const url = uploadUrl(filePath);
  const marker = "/api/uploads/";
  const idx = url.indexOf(marker);
  if (idx === -1) return url; // 不是本站 uploads 资源，原样返回
  return `${url.slice(0, idx)}/api/uploads/thumb/${url.slice(idx + marker.length)}?w=${width}`;
}

/**
 * 🔴 2026-10-16 外网预览版 URL（**客户端安全**）：`previewUrl(uploadUrl(videoPath))`。
 *
 * 用于**播放**场景（`<video src>`）：服务端按需转 720p/CRF28/+faststart 并缓存
 * （单镜 6.8MB→1.67MB、成片 104MB→24.4MB），跨境链路下起播与拖动显著改善。
 * **下载/取原片**仍用 `uploadUrl(...)`，不要替换。非 uploads 资源原样返回（外链安全）。
 */
export function previewUrl(filePath: string): string {
  if (!filePath) return "";
  const url = uploadUrl(filePath);
  const marker = "/api/uploads/";
  const idx = url.indexOf(marker);
  if (idx === -1) return url;
  // 幂等：已是预览 URL（或缩略图 URL）时原样返回，避免 preview/preview//thumb 叠加
  const after = url.slice(idx + marker.length);
  if (after.startsWith("preview/") || after.startsWith("thumb/")) return url;
  return `${url.slice(0, idx)}/api/uploads/preview/${after}`;
}
